// WHAT: The composition root — builds every service and wires them together.
// HOW:  Creates the stage from the flat board's bounds, builds the piece and
//       highlight layers, spins up the Stockfish worker, connects pointer
//       clicks → BoardPicker → GameLoop, and routes the selected area's
//       heights and features into BoardComposer, whose layout BoardScene
//       shows. The Phase 11 shell — menu, controls, record, game-over screen
//       and autosave — hangs off the same bus. Returns a handle so main.ts can
//       tear it all down on hot reload.
// WHY:  This is the one place where "which layout", "which piece factory",
//       "which engine", "which opponent" and "where saves live" are decided.
//       Phase 8's layout swap lives in BoardComposer; nothing in world/ or
//       game/ changed for it.

import { StockfishAI } from '@ai/StockfishAI';
import { FlatBoardLayout } from '@domain/board/FlatBoardLayout';
import type { IBoardLayout } from '@domain/board/IBoardLayout';
import { ChessEngine } from '@domain/chess/ChessEngine';
import type { GameEvents } from '@game/GameEvents';
import { GameLoop } from '@game/GameLoop';
import type { Players } from '@game/GameLoop';
import { SaveManager } from '@game/SaveManager';
import type { SaveContext } from '@game/SaveManager';
import { isSavedGame } from '@game/SavedGame';
import type { SavedGame } from '@game/SavedGame';
import { ThemeTracker } from '@game/ThemeTracker';
import { IndexedDbStore, STORES } from '@mapdata/cache/KeyValueStore';
import { ElevationLoader } from '@mapdata/elevation/ElevationLoader';
import type { IElevationView } from '@mapdata/elevation/ElevationLoader';
import { fixtureEntries } from '@mapdata/elevation/fixtureAreas';
import { FixtureElevationProvider } from '@mapdata/elevation/FixtureElevationProvider';
import { TerrariumElevationProvider } from '@mapdata/elevation/TerrariumElevationProvider';
import { FeatureLoader } from '@mapdata/features/FeatureLoader';
import type { IFeaturesView } from '@mapdata/features/FeatureLoader';
import {
  featureFixtureEntries,
  FixtureFeatureProvider,
} from '@mapdata/features/FixtureFeatureProvider';
import {
  isCachedFeatures,
  OverpassFeatureProvider,
} from '@mapdata/features/OverpassFeatureProvider';
import type { CachedFeatures } from '@mapdata/features/OverpassFeatureProvider';
import { buildBriefing } from '@mapdata/board/buildBriefing';
import type { Briefing, BriefingInputs } from '@mapdata/board/buildBriefing';
import { WikidataProvider } from '@mapdata/history/WikidataProvider';
import type { HistoryFact } from '@mapdata/history/historyFacts';
import { primaryPlaceName } from '@mapdata/features/primaryPlace';
import { NominatimGeocoder } from '@mapdata/geocode/NominatimGeocoder';
import type { HeightField } from '@mapdata/model/HeightField';
import type { MapFeature } from '@mapdata/model/MapFeature';
import type { SelectedArea } from '@mapdata/model/SelectedArea';
import { browserBase64 } from '@shared/encoding/base64';
import { EventBus } from '@shared/events/EventBus';
import { LocalJsonStore } from '@shared/storage/LocalJsonStore';
import { AreaBar } from '@ui/AreaBar';
import { AssessmentCard } from '@ui/AssessmentCard';
import { PlaceTitle } from '@ui/PlaceTitle';
import { CoachCard } from '@ui/CoachCard';
import { Attribution } from '@ui/Attribution';
import { BoardDebugPanel } from '@ui/BoardDebugPanel';
import { BriefingPanel } from '@ui/BriefingPanel';
import { ControlDock } from '@ui/ControlDock';
import { DataStatus } from '@ui/DataStatus';
import { FeaturesDebugPanel } from '@ui/FeaturesDebugPanel';
import { FpsMeter } from '@ui/FpsMeter';
import { GameControls } from '@ui/GameControls';
import { GameOverScreen } from '@ui/GameOverScreen';
import { HeightmapDebugPanel } from '@ui/HeightmapDebugPanel';
import { HintCard } from '@ui/HintCard';
import { IdentityCard } from '@ui/IdentityCard';
import { identityLine } from '@ui/identityLine';
import { KeyboardPlay } from '@ui/KeyboardPlay';
import { Announcer } from '@ui/Announcer';
import { MainMenu } from '@ui/MainMenu';
import { PanelColumn } from '@ui/PanelColumn';
import { PanelSheet } from '@ui/PanelSheet';
import type { NewGameRequest, SavedGameSummary } from '@ui/MainMenu';
import { PromotionPrompt } from '@ui/PromotionPrompt';
import { RecordPanel } from '@ui/RecordPanel';
import { Sounds } from '@ui/Sounds';
import { StatusBar } from '@ui/StatusBar';
import { Tooltips } from '@ui/Tooltips';
import { ViewControls } from '@ui/ViewControls';
import { BoardScene } from '@world/builders/BoardScene';
import { BoardView } from '@world/pieces/BoardView';
import { HighlightLayer } from '@world/pieces/HighlightLayer';
import { CaptureEffects } from '@world/pieces/CaptureEffects';
import { MoveAnimator } from '@world/pieces/MoveAnimator';
import { PieceLayer } from '@world/pieces/PieceLayer';
import { ModelPieceFactory } from '@world/pieces/ModelPieceFactory';
import { ProceduralPieceFactory } from '@world/pieces/ProceduralPieceFactory';
import { DEFAULT_PIECE_SET, isCompleteSet, loadPieceModels } from '@world/pieces/loadPieceModels';
import type { ModelKey, PieceModels } from '@world/pieces/loadPieceModels';
import type { Object3D } from 'three';
import { PIECE_SETS } from '@world/pieces/pieceSets';
import { EPISODE_REST_SECONDS, thunderEpisode, thundersIn } from '@world/scene/Atmosphere';
import type { Mood } from '@world/scene/Atmosphere';
import { BoardPicker } from '@world/scene/BoardPicker';
import { PointerInput } from '@world/scene/PointerInput';
import { WorldStage } from '@world/scene/WorldStage';

import { BoardComposer } from './BoardComposer';
import type { AppConfig } from './config';
import {
  CURATED_PLACES,
  ERA_LABEL,
  curatedByEra,
  curatedPlaceAt,
  isBattlefield,
  moodFor,
  namedBriefing,
} from './curatedPlaces';
import type { CuratedPlace } from './curatedPlaces';
import { choosePieceSet, previewSetFrom } from './pieceSetChoice';

/** Squares of water that make a board a shore, for the sound of a fine day. */
const SHORE_SQUARES = 3;

export interface AppHandle {
  /** The board currently shown — flat until the area's terrain has loaded, then warped. */
  layout(): IBoardLayout;
  readonly stage: WorldStage;
  selectedArea(): SelectedArea;
  heightField(): HeightField | null;
  features(): readonly MapFeature[] | null;
  /** The piece set the board is being dressed in: "medieval" unless it is a named battle. */
  pieceSet(): string;
  /** The weather being heard, if the sound is on and it is storming. */
  weatherSound(): Mood | null;
  /** How many times birds flying over have been heard. For tests. */
  birdCalls(): number;
  /** A lightning strike now, as the storm would send one — near or far. For tests. */
  strike(near: boolean): void;
  dispose(): void;
}

export function bootstrap(
  config: AppConfig,
  worldContainer: HTMLElement,
  uiContainer: HTMLElement,
  /** GLB pieces that loaded, if any. Missing ones stay procedural. */
  models: PieceModels = new Map(),
): AppHandle {
  // The flat board is the starting point; BoardComposer replaces it once terrain arrives.
  const initialLayout: IBoardLayout = new FlatBoardLayout({
    boardSizeMeters: config.boardSizeMeters,
  });
  const boardWidth = config.boardSizeMeters;
  const cellUnit = boardWidth / config.filesAndRanks;

  // --- world ---
  const stage = new WorldStage(worldContainer, initialLayout.bounds);

  const procedural = new ProceduralPieceFactory(cellUnit);
  /*
    Mixes the two: a model where one loaded, the drawn piece everywhere else.

    Always the modelled factory, even when the map it is handed is empty. The
    models are no longer required to be present before the board is built, and
    an empty factory delegates every piece to the procedural set until
    usePieceSet() fills it in.
  */
  const modelled = new ModelPieceFactory(models, procedural);
  const pieces = new PieceLayer(initialLayout, modelled);
  const highlights = new HighlightLayer(initialLayout);
  const animator = new MoveAnimator({ unit: cellUnit });
  const captures = new CaptureEffects(cellUnit);
  const view = new BoardView(pieces, highlights, animator, captures);
  stage.add(...view.objects);
  const stopAnimator = stage.loop.onTick((dt) => {
    animator.update(dt);
    captures.update(dt);
    highlights.update(dt);
  });

  // --- game ---
  const bus = new EventBus<GameEvents>();
  const engine = new ChessEngine();

  /*
    Pieces, by set. The board opens on the drawn pieces and dresses itself when
    the models land: the models are 11 MB, and every byte of that used to stand
    between the player and their first frame. A set that never arrives leaves
    drawn pieces on the board, which is what a failed load always meant.

    Which set is choosePieceSet's call — the original everywhere, an era's set
    on its own named battlefields once the set is whole, or whatever the
    address previews. Each set is fetched once and kept, so walking from Anzac
    Cove to Holy Island and back costs nothing the second time.

    A previewed set may be half made. Its missing pieces come from the original
    set, so a preview is always a full, playable board: the point is to see the
    new figures on real ground as they are finished, not to wait for all twelve.
  */
  const setFacts = {
    exists: (set: string) => Object.hasOwn(PIECE_SETS, set),
    complete: (set: string) => isCompleteSet(set),
  };
  const previewSet = previewSetFrom(window.location.search, setFacts.exists);
  if (previewSet !== null) {
    console.info(
      `Previewing the "${previewSet}" pieces; anything it lacks comes from "${DEFAULT_PIECE_SET}".`,
    );
  }
  const setLoads = new Map<string, Promise<PieceModels>>();
  let wantedSet: string | null = null;

  function loadSet(set: string): Promise<PieceModels> {
    let load = setLoads.get(set);
    if (load === undefined) {
      load = loadPieceModels(cellUnit, stage.renderer, set);
      setLoads.set(set, load);
    }
    return load;
  }

  function usePieceSet(set: string): void {
    if (set === wantedSet) return;
    wantedSet = set;
    const parts =
      set === DEFAULT_PIECE_SET ? [loadSet(set)] : [loadSet(DEFAULT_PIECE_SET), loadSet(set)];
    void Promise.all(parts)
      .then((maps) => {
        // A later board asked for a different set while this one downloaded.
        if (wantedSet !== set) return;
        // Later maps win, so the chosen set's figures cover the original's.
        const merged = new Map<ModelKey, Object3D>();
        for (const map of maps) for (const [key, model] of map) merged.set(key, model);
        if (merged.size === 0) return;
        modelled.replace(merged);
        // Rebuilt from the engine's position, the authority on where pieces are;
        // a move animating at this moment is cut short, not corrupted.
        view.showPosition(engine.pieces());
      })
      .catch((error: unknown) => {
        console.warn(
          `The "${set}" pieces could not be loaded; the board keeps what it has.`,
          error,
        );
      });
  }
  const promotion = new PromotionPrompt(uiContainer);
  const ai = new StockfishAI({ workerUrl: config.engineUrl, difficulty: config.defaultDifficulty });
  ai.ready().catch((error: unknown) => {
    // GameLoop falls back to a legal move on every failed request; this just tells the player why.
    console.error(
      'Chess engine failed to start; the computer will play weak fallback moves.',
      error,
    );
  });
  // Only the easiest level is questioned; startGame decides when.
  const game = new GameLoop({ engine, view, promotion, bus, ai });

  // --- persistence (Phase 11: one autosaved game, including its area) ---
  const saves = new SaveManager({
    bus,
    store: new LocalJsonStore<SavedGame>(config.saveKey, isSavedGame),
    context: (): SaveContext => ({
      area: areaBar.current,
      players: toPlayers(seating.humanColor),
      difficulty: seating.difficulty,
    }),
  });
  // Read once, before the first autosave overwrites it, so the menu can offer it.
  const savedOnLoad: SavedGame | null = saves.read();

  // The seating the shell will use for the next new game, and the save it is
  // still willing to resume. Both change only through startGame / resumeSavedGame.
  let seating: NewGameRequest = {
    humanColor: savedOnLoad === null ? config.defaultHumanColor : toHumanColor(savedOnLoad.players),
    difficulty: savedOnLoad?.difficulty ?? config.defaultDifficulty,
  };
  let resumable: SavedGame | null = savedOnLoad;
  /** What the current board is called, once its features have arrived. */
  let placeName: string | null = null;
  /** The area the board is being built for, so a finished board knows what it is. */
  let boardArea: SelectedArea = savedOnLoad?.area ?? config.defaultArea;
  /** A place chosen by name whose title is still to be shown, once its board is ready. */
  let pendingTitle: CuratedPlace | null = null;
  /** Drops a Wikidata answer that arrives after the player has moved on. */
  let briefingGeneration = 0;

  // --- ui ---
  const statusBar = new StatusBar(uiContainer, bus);
  // One tooltip for every `data-tip` in the overlay, placed where it fits.
  const tooltips = new Tooltips(uiContainer);
  // Silent until the player asks; a browser will not start audio otherwise.
  const sounds = new Sounds(bus);
  // Required by the ODbL for as long as the board is on screen.
  const attribution = new Attribution(uiContainer);
  const themeTracker = new ThemeTracker(bus);
  // Everything else the game says, it says by drawing; this says it in words.
  const announcer = new Announcer(uiContainer, bus);
  /*
    The panels mount into the sheet rather than straight into #ui. Above the
    breakpoint every box it adds is `display: contents`, so they go on
    positioning themselves against #ui and the desktop layout is byte-for-byte
    what it was; below it, they become a drawer under a bar (§13.3).
  */
  const sheet = new PanelSheet(uiContainer, {
    onMenu: () => {
      menu.open();
    },
    onTips: () => {
      void game.requestHint();
    },
    onUndo: () => {
      game.undo();
    },
    onTopDown: () => {
      stage.lookDown();
    },
    onShow: (tab) => {
      // Asking for the field is already the request to read it.
      if (tab === 'field') briefing.expand();
    },
  });
  const briefing = new BriefingPanel(sheet.fieldSlot);
  // The reveal is drawn on the briefing's paper rather than floating over the board.
  const identityCard = new IdentityCard(briefing.selectionSlot, bus, themeTracker);
  // The same line, in the one place a phone can see it while the drawer is shut.
  const stopPeek = bus.on('selection-changed', ({ square }) => {
    // A selected square keeps its letter and number lit, so a beginner can
    // read where the piece they picked up stands.
    boardScene.setSelectedSquare(square);
    sheet.setPeek(square === null ? null : identityLine(themeTracker.describe(square)));
  });
  // The same verdict the card carries, in the one place a phone can read it
  // while the drawer is shut. Both listen to the event rather than to each
  // other, so neither has to exist for the other to work.
  const stopStanding = bus.on('assessment-changed', ({ assessment }) => {
    sheet.setStanding(assessment);
  });
  // Optional, CC0, and never allowed to hold the board up: the briefing is
  // shown at once from OSM alone, then re-shown if Wikidata answers.
  const historyProvider = new WikidataProvider(
    new IndexedDbStore<HistoryFact>(STORES.history, isHistoryFact),
  );
  // Left column, top to bottom: the controls, the record, then a hint when
  // there is one. Flex does the arithmetic that three absolute positions used
  // to have to agree on.
  const column = new PanelColumn(sheet.gameSlot);
  const dock = new ControlDock(column.element);
  const coachCard = new CoachCard(column.element, bus);
  const assessment = new AssessmentCard(column.element, bus);
  const record = new RecordPanel(column.element, bus, themeTracker);
  const hintCard = new HintCard(column.element, bus, themeTracker);

  const menu = new MainMenu(uiContainer, seating, {
    onNewGame: (request) => {
      startGame(request);
    },
    onResume: () => {
      resumeSavedGame();
    },
    onChooseArea: () => {
      areaBar.open();
    },
    onChooseField: () => {
      areaBar.openPresets();
    },
    famousFields: CURATED_PLACES.filter(isBattlefield).map((p) => p.name),
    // The name if the map knows one; the coordinates are the fallback, not the point.
    areaLabel: () => placeName ?? areaBar.label,
    savedGame: () => summarise(resumable),
    // Nothing plays behind the front door — see GameLoop.setAtMenu.
    onVisibility: (open) => {
      game.setAtMenu(open);
    },
  });
  const controls = new GameControls({ menu: dock.placeSlot, actions: dock.actionSlot }, bus, {
    onMenu: () => {
      menu.open();
    },
    onTogglePause: () => {
      game.setPaused(!game.isPaused);
    },
    canPause: () => game.isWatching() && game.outcome === null,
    isPaused: () => game.isPaused,
    onHint: () => {
      void game.requestHint();
    },
    onUndo: () => {
      game.undo();
    },
    onResign: () => {
      // Resigning on behalf of whoever the player is sitting as.
      game.resign(resigningColor());
    },
    canHint: () => game.canHint(),
    canUndo: () => game.canUndo(),
    canResign: () => game.outcome === null && seating.humanColor !== 'none',
    onWaitingChanged: (waiting) => {
      sheet.setTipsWaiting(waiting);
    },
  });
  const placeTitle = new PlaceTitle(uiContainer);
  const gameOver = new GameOverScreen(uiContainer, bus, themeTracker, () => {
    menu.open();
  });

  const debug = new URLSearchParams(window.location.search).has('debug');
  const fps = debug
    ? new FpsMeter(uiContainer, () => ({
        calls: stage.renderer.info.render.calls,
        triangles: stage.renderer.info.render.triangles,
      }))
    : null;
  const stopFps =
    fps === null
      ? () => undefined
      : stage.loop.onTick((dt) => {
          fps.tick(dt);
        });

  /** The area the board is currently being built for; see `loadArea`. */
  let areaGeneration = 0;

  // --- map area + elevation (Phase 5/6: selected, fetched and shown; consumed by the board from Phase 8) ---
  // Fixtures first (offline), then IndexedDB-cached Terrarium tiles (D-022).
  const elevationProvider = new FixtureElevationProvider(
    fixtureEntries(),
    new TerrariumElevationProvider(
      new IndexedDbStore<Float32Array>(STORES.elevationTiles, (v) => v instanceof Float32Array),
    ),
    browserBase64,
  );
  // Loading and failure are visible to every player; the debug panels add the detail.
  const dataStatus = new DataStatus(
    uiContainer,
    () => {
      loadArea(areaBar.current);
    },
    () => {
      areaBar.open();
    },
    () => {
      /*
        The ground arrived and the map did not. Composing with no features at
        all gives a board terraced to the real hills, with names made from the
        shape of the land rather than from what stands on it — less than this
        game is meant to be, which is why it is offered rather than taken.
      */
      composer.setFeatures(areaGeneration, []);
      dataStatus.dismiss();
    },
  );
  const heightmapPanel = debug ? new HeightmapDebugPanel(uiContainer) : null;
  const elevationView: IElevationView = {
    showLoading: (done, total) => {
      dataStatus.terrain.showLoading(done, total);
      heightmapPanel?.showLoading(done, total);
    },
    showField: (result, elapsedMs) => {
      dataStatus.terrain.showField(result, elapsedMs);
      heightmapPanel?.showField(result, elapsedMs);
    },
    showError: (message) => {
      dataStatus.terrain.showError(message);
      heightmapPanel?.showError(message);
    },
  };
  const elevation = new ElevationLoader(elevationProvider, elevationView);

  // Features: fixtures first (offline), then IndexedDB-cached, rate-limited Overpass (D-024).
  const featureProvider = new FixtureFeatureProvider(
    featureFixtureEntries(),
    new OverpassFeatureProvider(
      new IndexedDbStore<CachedFeatures>(STORES.features, isCachedFeatures),
    ),
  );
  const featuresPanel = debug ? new FeaturesDebugPanel(uiContainer) : null;
  const featuresView: IFeaturesView = {
    showLoading: () => {
      dataStatus.features.showLoading();
      featuresPanel?.showLoading();
    },
    showFeatures: (summary, result) => {
      dataStatus.features.showFeatures(summary, result);
      featuresPanel?.showFeatures(summary, result);
    },
    showError: (message) => {
      dataStatus.features.showError(message);
      featuresPanel?.showError(message);
    },
  };
  const features = new FeatureLoader(featureProvider, featuresView);

  const areaBar: AreaBar = new AreaBar(
    { summary: briefing.coordinateSlot, buttons: dock.placeSlot },
    savedOnLoad?.area ?? config.defaultArea,
    {
      geocoder: new NominatimGeocoder(),
      styleUrl: config.mapStyleUrl,
      pickerHost: uiContainer,
      presets: curatedByEra(),
      onAreaChanged: (area) => {
        placeName = null;
        // Chosen by name, or landed exactly on a named place: either way, announce it.
        pendingTitle = curatedPlaceAt(area);
        placeTitle.hide();
        // A new place is something to look at: out of the drawer, onto the board.
        sheet.close();
        loadArea(area);
        // A new area is a new board, so it is a new game: identities from the old
        // map would otherwise follow pieces onto ground they never came from.
        startGame(seating);
      },
    },
  );

  // --- input ---
  const picker = new BoardPicker(stage.camera, initialLayout, null, pieces);
  const input = new PointerInput(stage.renderer.domElement);
  input.onClick((ndc) => {
    if (menu.isOpen) return;
    const square = picker.pick(ndc);
    if (square !== null) void game.handleSquareClick(square);
  });
  input.onMove((ndc) => {
    boardScene.setHoveredSquare(ndc === null || menu.isOpen ? null : picker.pick(ndc));
  });

  /*
    The same board, named rather than pointed at. Enter goes through the very
    same entry point a click does, so selecting, moving, capturing, castling on
    the rook and the promotion prompt need no second implementation.
  */
  const keyboard = new KeyboardPlay({
    onActivate: (square) => {
      void game.handleSquareClick(square);
    },
    onCancel: () => {
      game.clearSelection();
    },
    onCursorMoved: (square) => {
      highlights.setCursor(square);
      boardScene.setHoveredSquare(square);
    },
    isBlocked: () => menu.isOpen || areaBar.isPickerOpen,
  });

  // --- board: flat until terrain arrives, then warped (Phase 8) ---
  const boardScene = new BoardScene({ stage, pieces, highlights, picker });
  /*
    Coordinates are sized in metres, so they shrink with the board, and a phone
    shows the board small: at desktop size they could not be read on a 390 px
    screen. The same breakpoint the stylesheet uses for the phone layout.
  */
  const narrow = window.matchMedia('(max-width: 880px), (max-height: 520px)');
  const fitCoordinates = (): void => {
    boardScene.setCoordinateScale(narrow.matches ? 1.8 : 1);
    // Fewer trees on a phone: a forest is texture at that size, and a phone's
    // GPU is the one most likely to feel two thousand of them.
    boardScene.setTreeCap(narrow.matches ? 1200 : 2500);
  };
  fitCoordinates();
  narrow.addEventListener('change', fitCoordinates);
  const composer = new BoardComposer(config.boardSizeMeters, ({ model, mode }) => {
    /*
      A battlefield is headed with its battle. Derived from the area itself, so a
      resumed game at Gettysburg is called Gettysburg with nothing extra saved,
      and a square dragged off the ridge stops claiming the name.
    */
    const curated = curatedPlaceAt(boardArea);
    const battle = curated !== null && isBattlefield(curated) ? curated : null;
    const fieldName = model.features === null ? null : primaryPlaceName(model.features);
    const titled = (b: Briefing): Briefing =>
      battle === null ? b : namedBriefing(b, battle, fieldName);
    // The flat placeholder board carries no features; keep the last known name.
    if (model.features !== null) placeName = battle?.name ?? fieldName;
    const inputs: BriefingInputs | null =
      model.features === null || model.heights === null || model.cover === null
        ? null
        : {
            features: model.features,
            heights: model.heights,
            cover: model.cover,
            bounds: model.layout.bounds,
          };
    briefing.show(inputs === null ? null : titled(buildBriefing(inputs)));
    if (inputs !== null) {
      const generation = ++briefingGeneration;
      void historyProvider.factsFor(inputs.features).then((history) => {
        // A later board may have arrived while Wikidata was answering.
        if (generation !== briefingGeneration || history.size === 0) return;
        briefing.show(titled(buildBriefing({ ...inputs, history })));
      });
    }
    boardScene.show(model);
    themeTracker.setTheme(model.theme);
    // A move sounds like the ground it lands on; a flat board has no ground to tell.
    const cover = model.cover;
    sounds.setGround((square) => cover?.get(square) ?? null);
    captures.setGround((square) => cover?.get(square) ?? null);
    // A board with water on a few of its squares is by the sea or a loch: a
    // fine day there sounds of waves and gulls.
    let wet = 0;
    for (const kind of cover?.values() ?? []) if (kind === 'water') wet += 1;
    sounds.setShore(wet >= SHORE_SQUARES);
    // The arrival, marked once: when the chosen place's board is actually there.
    if (
      model.features !== null &&
      pendingTitle !== null &&
      sameArea(pendingTitle.area, boardArea)
    ) {
      const kicker = isBattlefield(pendingTitle) ? ERA_LABEL[pendingTitle.era] : null;
      placeTitle.show({ kicker, name: pendingTitle.name, line: pendingTitle.blurb });
      announcer.say(
        `${pendingTitle.name}${kicker === null ? '' : `, ${kicker}`}. ${pendingTitle.blurb.replace(' · ', ', ')}.`,
      );
      pendingTitle = null;
    }
    // Positions changed under the pieces; re-place them from the engine's position.
    view.showPosition(engine.pieces());
    const t = model.terrain;
    console.info(
      `Board: ${mode}${t === null ? '' : ` (${String(t.lines.length)} lines, ${String(t.points.length)} points)`}`,
    );
  });

  function loadArea(area: SelectedArea): void {
    boardArea = area;
    // Each board opens in its own weather; the button shows it and can change it.
    const mood = moodFor(area);
    stage.setMood(mood);
    viewControls.setMood(mood);
    sounds.setWeather(mood);
    usePieceSet(choosePieceSet(area, previewSet, setFacts));
    const generation = composer.beginArea();
    areaGeneration = generation;
    void elevation.load(area).then((result) => {
      if (result !== null) composer.setHeights(generation, result.field);
    });
    void features.load(area).then((result) => {
      if (result !== null) composer.setFeatures(generation, result.features);
    });
  }

  function startGame(request: NewGameRequest): void {
    resumable = null;
    seating = request;
    ai.setDifficulty(request.difficulty);
    game.newGame(toPlayers(request.humanColor));
  }

  function resumeSavedGame(): void {
    const saved = resumable;
    if (saved === null) return;
    resumable = null;
    seating = { humanColor: toHumanColor(saved.players), difficulty: saved.difficulty };
    ai.setDifficulty(saved.difficulty);
    if (!sameArea(saved.area, areaBar.current)) {
      areaBar.setArea(saved.area);
      loadArea(saved.area);
    }
    saves.resume(game);
  }

  function resigningColor(): 'white' | 'black' {
    // Hot-seat: whoever is to move gives up. Otherwise the player's own colour.
    if (seating.humanColor === 'white' || seating.humanColor === 'black') return seating.humanColor;
    return engine.turn;
  }

  const boardDebug = debug
    ? new BoardDebugPanel(
        uiContainer,
        { mode: 'warped', labels: true, features: true },
        (state) => {
          composer.setMode(state.mode);
          boardScene.setOverlay({ labels: state.labels, features: state.features });
        },
      )
    : null;
  if (debug) boardScene.setOverlay({ labels: true, features: true });

  const viewControls = new ViewControls(dock.actionSlot, {
    onLabelsChanged: (mode) => {
      boardScene.setLabelMode(mode);
    },
    onAssessingChanged: (on) => {
      game.setAssessing(on);
    },
    onCoordinatesChanged: (on) => {
      boardScene.setCoordinatesVisible(on);
    },
    onSceneryChanged: (on) => {
      boardScene.setSceneryVisible(on);
    },
    onMoodChanged: (mood) => {
      stage.setMood(mood);
      sounds.setWeather(mood);
    },
    onCoachingChanged: (on) => {
      game.setCoaching(on);
    },
    onSoundChanged: (on) => {
      sounds.setEnabled(on);
    },
    onRecenter: () => {
      stage.reframe(boardScene.layout?.bounds ?? initialLayout.bounds);
    },
    onTopDown: () => {
      stage.lookDown();
    },
  });

  /*
    The storm comes in episodes, as the author described it: a strike close
    by — lit hard, its thunder right on top of you — then ten to fifteen
    seconds later two far off, a few seconds apart, each heard a moment after
    its light. Then a quiet spell, and another. Strikes are never closer than
    MIN_STRIKE_GAP_SECONDS, which keeps the flashes inside the photosensitivity
    guideline. The stage decides whether a strike is seen — never for a player
    who has asked for less motion — and the thunder follows either way: sound
    is not motion, and a storm you cannot see can still be heard.
  */
  const strikeTimers: number[] = [];
  const strike = (near: boolean): void => {
    if (!thundersIn(stage.mood)) return;
    stage.flash(near);
    sounds.thunder(near, near ? 0.1 : 0.8 + Math.random() * 1.8);
  };
  const scheduleEpisode = (delaySeconds: number): void => {
    strikeTimers.push(
      window.setTimeout(() => {
        strikeTimers.length = 0;
        const episode = thunderEpisode();
        for (const { at, near } of episode) {
          strikeTimers.push(
            window.setTimeout(() => {
              strike(near);
            }, at * 1000),
          );
        }
        const { min, max } = EPISODE_REST_SECONDS;
        const last = episode[episode.length - 1]?.at ?? 0;
        scheduleEpisode(last + min + Math.random() * (max - min));
      }, delaySeconds * 1000),
    );
  };
  scheduleEpisode(6 + Math.random() * 6);

  // The rain's sound follows the rain on screen, a few times a second.
  let sinceRainSound = 0;
  // Birds flying over are heard as they come, if the sound is on.
  const stopBirdCalls = stage.onBirds((kind) => {
    sounds.birdCall(kind);
  });

  const stopRainSound = stage.loop.onTick((dt) => {
    sinceRainSound += dt;
    if (sinceRainSound < 0.25) return;
    sinceRainSound = 0;
    sounds.setRainIntensity(stage.rainIntensity);
  });

  loadArea(areaBar.current);
  // The menu goes up first so the hold is already on when the game starts:
  // otherwise a watched game announces "White is thinking…" on its way to
  // being held, and the status bar keeps saying it behind the front door.
  menu.open();
  game.start(toPlayers(seating.humanColor));
  stage.start();

  return {
    layout: () => boardScene.layout ?? initialLayout,
    stage,
    selectedArea: () => areaBar.current,
    heightField: () => elevation.heightField,
    features: () => features.features,
    pieceSet: () => wantedSet ?? DEFAULT_PIECE_SET,
    weatherSound: () => sounds.ambient,
    birdCalls: () => sounds.birdCalls,
    strike,
    dispose: () => {
      input.dispose();
      boardDebug?.dispose();
      boardScene.dispose();
      features.dispose();
      featuresPanel?.dispose();
      elevation.dispose();
      heightmapPanel?.dispose();
      dataStatus.dispose();
      areaBar.dispose();
      stopFps();
      fps?.dispose();
      viewControls.dispose();
      gameOver.dispose();
      placeTitle.dispose();
      controls.dispose();
      menu.dispose();
      hintCard.dispose();
      record.dispose();
      assessment.dispose();
      coachCard.dispose();
      dock.dispose();
      column.dispose();
      stopPeek();
      for (const timer of strikeTimers) window.clearTimeout(timer);
      stopRainSound();
      stopBirdCalls();
      narrow.removeEventListener('change', fitCoordinates);
      stopStanding();
      keyboard.dispose();
      announcer.dispose();
      sheet.dispose();
      briefing.dispose();
      saves.dispose();
      identityCard.dispose();
      themeTracker.dispose();
      attribution.dispose();
      sounds.dispose();
      tooltips.dispose();
      statusBar.dispose();
      ai.dispose();
      stopAnimator();
      highlights.dispose();
      modelled.dispose();
      bus.clear();
      stage.dispose();
    },
  };
}

/** Guards a cached fact against a shape written by an older version. */
function isHistoryFact(value: unknown): value is HistoryFact {
  if (typeof value !== 'object' || value === null) return false;
  const f = value as Partial<HistoryFact>;
  return (
    typeof f.id === 'string' &&
    (f.label === null || typeof f.label === 'string') &&
    (f.year === null || typeof f.year === 'number') &&
    (f.kind === null || typeof f.kind === 'string') &&
    (f.heritage === null || typeof f.heritage === 'string')
  );
}

function toPlayers(humanColor: NewGameRequest['humanColor']): Players {
  switch (humanColor) {
    case 'white':
      return { white: 'human', black: 'ai' };
    case 'black':
      return { white: 'ai', black: 'human' };
    case 'both':
      return { white: 'human', black: 'human' };
    case 'none':
      return { white: 'ai', black: 'ai' };
  }
}

function toHumanColor(players: Players): NewGameRequest['humanColor'] {
  if (players.white === 'human' && players.black === 'human') return 'both';
  if (players.white === 'human') return 'white';
  if (players.black === 'human') return 'black';
  return 'none';
}

function summarise(saved: SavedGame | null): SavedGameSummary | null {
  if (saved === null) return null;
  return {
    moveCount: saved.moves.length,
    areaLabel: `${saved.area.centerLat.toFixed(3)}, ${saved.area.centerLon.toFixed(3)}`,
    savedAt: new Date(saved.savedAt),
  };
}

function sameArea(a: SelectedArea, b: SelectedArea): boolean {
  return (
    a.centerLat === b.centerLat &&
    a.centerLon === b.centerLon &&
    a.sizeMeters === b.sizeMeters &&
    a.rotationDeg === b.rotationDeg
  );
}
